import { useEffect, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { inputClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  getManufacturingOverheadCogsLink,
  saveManufacturingOverheadCogsLink,
  type MfgOverheadCogsPreview,
} from "@/lib/manufacturing/overheadClient";
import {
  OVERHEAD_COGS_BASIS_OPTIONS,
  overheadGoalFieldLabel,
  overheadGoalInputValue,
  overheadGoalRateFromInput,
  type OverheadCogsBasis,
  type OverheadVarianceDisplay,
} from "@/lib/manufacturing/overheadCogsLink";

export function ManufacturingOverheadCogsLinkCard({
  enabled,
  plantId,
  periodStart,
}: {
  enabled: boolean;
  plantId: string;
  periodStart: string;
}) {
  const [basis, setBasis] = useState<OverheadCogsBasis | "">("");
  const [goalInput, setGoalInput] = useState("");
  const [varianceDisplay, setVarianceDisplay] = useState<OverheadVarianceDisplay>("percent");
  const [showOnCogs, setShowOnCogs] = useState(false);
  const [preview, setPreview] = useState<MfgOverheadCogsPreview | null>(null);
  const [missingTable, setMissingTable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void (async () => {
      const loaded = await getManufacturingOverheadCogsLink({
        plant_id: plantId || undefined,
        period_start: periodStart,
      });
      if (cancelled) return;
      if (!loaded.ok) {
        setError(loaded.error);
        return;
      }
      setBasis(loaded.data.settings.basis ?? "");
      setGoalInput(overheadGoalInputValue(loaded.data.settings.basis, loaded.data.settings.goal_rate ?? null));
      setVarianceDisplay(loaded.data.settings.variance_display);
      setShowOnCogs(loaded.data.settings.show_on_cogs);
      setPreview(loaded.data.preview);
      setMissingTable(loaded.data.missing_table === true);
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, plantId, periodStart]);

  return (
    <SectionCard
      title="COGS column"
      defaultCollapsed
      description="Choose how the overhead pool is spread across product SKUs. Options and packages are not charged. Checking the box adds one COGS column you can rename and move within COGS."
    >
      <div className="grid gap-3">
        <label className="block min-w-0 text-sm">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
            Calculation
          </span>
          <select
            className={`${inputClass} w-full`}
            value={basis}
            onChange={(event) => {
              const next = event.target.value as OverheadCogsBasis | "";
              const wasPercent = basis === "materials_labor";
              const nextPercent = next === "materials_labor";
              if (Boolean(basis) && Boolean(next) && wasPercent !== nextPercent) setGoalInput("");
              if (!next) setGoalInput("");
              setBasis(next);
              if (!next) setShowOnCogs(false);
            }}
            disabled={!enabled || busy}
          >
            <option value="">Choose a rule</option>
            {OVERHEAD_COGS_BASIS_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        {basis ? (
          <label className="block min-w-0 text-sm">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
              {overheadGoalFieldLabel(basis)}
            </span>
            <input
              type="number"
              min={0}
              step={basis === "materials_labor" ? "0.1" : "0.01"}
              className={`${inputClass} w-full`}
              value={goalInput}
              onChange={(event) => setGoalInput(event.target.value)}
              disabled={!enabled || busy}
            />
            <span className="mt-1 block text-xs text-gray-500 dark:text-zinc-400">
              COGS charges this goal. Variance shows how far booked actuals land from it once a build is completed.
            </span>
          </label>
        ) : null}
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
            Variance
          </span>
          <select
            className={`${inputClass} min-w-[8rem]`}
            value={varianceDisplay}
            onChange={(event) => setVarianceDisplay(event.target.value as OverheadVarianceDisplay)}
            disabled={!enabled || busy}
          >
            <option value="percent">Percent</option>
            <option value="dollars">Dollars</option>
          </select>
        </label>
        <label className="flex items-center gap-2 pb-2 text-sm text-gray-800 dark:text-zinc-200">
          <input
            type="checkbox"
            checked={showOnCogs}
            onChange={(event) => setShowOnCogs(event.target.checked)}
            disabled={!enabled || busy || !basis}
          />
          Show on COGS
        </label>
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          disabled={!enabled || busy}
          onClick={() => {
            const goalRate = basis ? overheadGoalRateFromInput(basis, goalInput) : null;
            if (goalRate === "invalid") {
              setError("Goal must be zero or more.");
              return;
            }
            setBusy(true);
            setError(null);
            void (async () => {
              const saved = await saveManufacturingOverheadCogsLink({
                basis: basis || null,
                show_on_cogs: showOnCogs,
                variance_display: varianceDisplay,
                goal_rate: goalRate,
                plant_id: plantId || undefined,
                period_start: periodStart,
              });
              setBusy(false);
              if (!saved.ok) {
                setError(saved.error);
                return;
              }
              setBasis(saved.data.settings.basis ?? "");
              setGoalInput(overheadGoalInputValue(saved.data.settings.basis, saved.data.settings.goal_rate ?? null));
              setVarianceDisplay(saved.data.settings.variance_display);
              setShowOnCogs(saved.data.settings.show_on_cogs);
              setPreview(saved.data.preview);
              setMissingTable(false);
            })();
          }}
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
      {missingTable ? (
        <p className="mt-3 text-sm text-amber-800 dark:text-amber-200">
          Apply docs/sql/mfg_overhead_cogs_settings_v1.sql before this setting can be saved.
        </p>
      ) : null}
      {error ? <p className="mt-3 text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      {preview ? <p className="mt-3 text-sm text-gray-600 dark:text-zinc-300">{preview.note}</p> : null}
    </SectionCard>
  );
}
